// src/pages/BookManagement.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/BookManagement.css";
import AdminSidebar from "../components/AdminSidebar.js";
import Header from "../components/Header.js";
import IDCardPopup from "../components/IDCardPopup.js";
import DeletePopup from "../components/DeletePopup.js";
import { getToken } from "../storage/Storage.js";
import { fetchAllBooks, deleteBook } from "../services/AllServices.js";
import delIcon from "../images/delIcon.png";

// Set this to your media base URL or use env var
const BASE_MEDIA_URL = process.env.REACT_APP_MEDIA_URL || "https://your-media-base.example.com/";

const BookManagement = () => {
  const [activeItem, setActiveItem] = useState("bookManagement");
  const [booksData, setBooksData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [openIDCardPopup, setOpenIDCardPopup] = useState(false);
  const [idCardUrl, setIdCardUrl] = useState("");
  const [token, setTokenState] = useState(null);
  const [loading, setLoading] = useState(false);

  // delete popup state
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);

  const navigate = useNavigate();

  // helpers
  const getAuthorString = (book) => {
    if (!book) return "-";
    const list = book.author_list || book.authors || [];
    if (Array.isArray(list) && list.length > 0) return list.join(", ");
    return book.author || book.author_name || "-";
  };

  const getThumbnailUrl = (book) => {
    if (!book) return "";
    const candidates = [book.thumbnail, book.thumbnail_url, book.cover, book.image];
    const val = candidates.find((c) => c && typeof c === "string");
    if (!val) return "";
    if (val.startsWith("http://") || val.startsWith("https://")) return val;
    const base = BASE_MEDIA_URL.endsWith("/") ? BASE_MEDIA_URL : BASE_MEDIA_URL + "/";
    const path = val.startsWith("/") ? val.slice(1) : val;
    return base + path;
  };

  // fetch books
  useEffect(() => {
    const fetchBooksData = async () => {
      try {
        setLoading(true);
        const response = await fetchAllBooks(token);
        if (response?.status === "success" && Array.isArray(response.data)) {
          setBooksData(response.data.reverse());
        } else {
          // handle unexpected shapes
          setBooksData(response?.data || []);
        }
      } catch (error) {
        console.error("Error fetching books:", error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchBooksData();
  }, [token]);

  // get token once
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const storedToken = await getToken();
        setTokenState(storedToken);
      } catch (error) {
        console.error("Failed to fetch token", error);
      }
    };
    fetchToken();
  }, []);

  // open delete popup
  const openDeletePopup = (book) => {
    setSelectedBook(book);
    setIsDeletePopupOpen(true);
  };

  // confirm delete from popup
  const confirmDeleteBook = async () => {
    if (!selectedBook) {
      setIsDeletePopupOpen(false);
      return;
    }

    try {
      const prevBooks = [...booksData];
      // optimistic removal
      setBooksData((prev) => prev.filter((b) => b._id !== selectedBook._id));

      const res = await deleteBook({ bookID: selectedBook._id, token });
      if (res?.status !== "success") {
        // rollback on failure
        setBooksData(prevBooks);
        console.error("Failed to delete book:", res);
      }
    } catch (error) {
      console.error("Error deleting book:", error);
      // optionally rollback
    } finally {
      setIsDeletePopupOpen(false);
      setSelectedBook(null);
    }
  };

  // thumbnail click
  const handleThumbnailClick = (book) => {
    const url = getThumbnailUrl(book);
    setIdCardUrl(url);
    setOpenIDCardPopup(true);
  };

  // header/profile interactions
  const handleProfileClick = () => {
    navigate("/settings", { state: { selectedSection: "account" } });
  };

  const resetSearch = () => setSearchQuery("");

  const handleActiveItemChange = (item) => {
    setActiveItem(item);
    if (item === "bookManagement") navigate("/Book-Management");
    else if (item === "userManagement") navigate("/User-Management");
    resetSearch();
  };

  // filter by title or author_list
  const filteredBooks = booksData.filter((b) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const title = (b.title || "").toLowerCase();
    const authors = (b.author_list || []).join(" ").toLowerCase() || (b.author || "").toLowerCase();
    return title.includes(q) || authors.includes(q);
  });

  // header visibility logic (same as before)
  const getHeaderVisibility = () => {
    if (activeItem === "bookManagement" || activeItem === "") {
      return { showSearch: false, showUserProfile: true, showArrows: false, pageName: "Book Management" };
    } else if (activeItem === "userManagement") {
      return { showSearch: false, showUserProfile: true, showArrows: false, pageName: "User Management" };
    }
  };

  const { showSearch, showUserProfile, showArrows, pageName } = getHeaderVisibility();

  return (
    <main className="main-content">
      <div className="sidebar_container">
        <AdminSidebar activeItem={activeItem} setActiveItem={handleActiveItemChange} resetSearch={resetSearch} />
      </div>

      <div className="dashboard_container">
        <div className="header_container">
          <Header
            showSearch={showSearch}
            showUserProfile={showUserProfile}
            showArrows={showArrows}
            pageName={pageName}
            searchQuery={searchQuery}
            onSearch={(query) => setSearchQuery(query)}
            onProfileClick={handleProfileClick}
          />
        </div>

        <div className="table_container">
          {loading ? (
            <div className="loading">Loading books...</div>
          ) : (
            <table className="users_table">
              <thead>
                <tr>
                  <th>Thumbnail</th>
                  <th>Title</th>
                  <th>Author(s)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBooks.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: "16px" }}>
                      No books found.
                    </td>
                  </tr>
                ) : (
                  filteredBooks.map((book) => {
                    const thumbUrl = getThumbnailUrl(book);
                    return (
                      <tr key={book._id}>
                        <td>
                          <img
                            src={thumbUrl || "https://via.placeholder.com/60x80"}
                            alt={book.title || "Book"}
                            className="avatar"
                            onClick={() => handleThumbnailClick(book)}
                            style={{ cursor: "pointer", width: 60, height: 80, objectFit: "cover" }}
                          />
                        </td>
                        <td>{book.title || "-"}</td>
                        <td>{getAuthorString(book)}</td>
                        <td>
                          <button
                            className="delete_icon_btn"
                            onClick={() => openDeletePopup(book)}
                            title="Delete book"
                            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6 }}
                          >
                            <img src={delIcon} alt="Delete" style={{ width: 20, height: 20 }} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Delete confirmation popup */}
      <DeletePopup
        open={isDeletePopupOpen}
        onClose={() => {
          setIsDeletePopupOpen(false);
          setSelectedBook(null);
        }}
        onConfirm={confirmDeleteBook}
        bookTitle={selectedBook?.title}
      />

      {/* Thumbnail preview popup */}
      <IDCardPopup open={openIDCardPopup} idCardUrl={idCardUrl} onClose={() => setOpenIDCardPopup(false)} />
    </main>
  );
};

export default BookManagement;
